/**
 * Octo AI paneli — sağdan açılan sohbet.
 *
 * Masaüstünde sabit genişlikte bir kenar paneli (sürüklenerek genişletilebilir,
 * genişlik kalıcı), mobilde TAM EKRAN: dar ekranda 400 piksellik bir panelde ne sohbet
 * ne de AI'ın ürettiği rapor okunuyor.
 *
 * Panel sekmelerden bağımsız yaşıyor (durum `aiStore`'da): kullanıcı ekran değiştirse
 * de akış kesilmiyor, paneli kapatıp açtığında yanıt yerinde duruyor.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Plus, Settings, X } from 'lucide-react';
import { useTabsStore } from '@/features/shell/tabs/tabsStore';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { Alert } from '@/shared/ui/Alert';
import { OctoMark } from '@/shared/ui/OctoMark';
import { Spinner } from '@/shared/ui/Spinner';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { useAuthStore } from '@/entities/session/model/authStore';
import { toast } from '@/shared/ui/toastStore';
import type { AiConversation } from '@/shared/types/aiChat';
import {
  useAiConversations,
  useAiMessages,
  useDeleteConversation,
  useRenameConversation,
} from '../api/aiApi';
import { MAX_WIDTH, MIN_WIDTH, newMessageId, useAiStore } from '../model/aiStore';
import { artifactFromDto, extractArtifacts } from '../model/artifacts';
import { useAiChat } from '../model/useAiChat';
import { AiComposer } from './AiComposer';
import { AiHistoryList } from './AiHistoryList';
import { AiMessageBubble } from './AiMessageBubble';
import { AiQuotaBar } from './AiQuotaBar';

export function AiChatPanel({
  serverReady = true,
  onClose,
}: {
  /** Sunucuda Claude Code yapılandırılmış mı; değilse sohbet yerine sebep gösterilir. */
  serverReady?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const isSysRole = useAuthStore((s) => !!s.user?.isSysRol);
  const openCustomTab = useTabsStore((s) => s.openCustom);
  const { send, stop } = useAiChat();

  const width = useAiStore((s) => s.width);
  const setWidth = useAiStore((s) => s.setWidth);
  const messages = useAiStore((s) => s.messages);
  const streaming = useAiStore((s) => s.streaming);
  const activeTool = useAiStore((s) => s.activeTool);
  const quotaExceeded = useAiStore((s) => s.quotaExceeded);
  const conversationId = useAiStore((s) => s.conversationId);
  const newChat = useAiStore((s) => s.newChat);
  const openConversation = useAiStore((s) => s.openConversation);

  const [historyOpen, setHistoryOpen] = useHistoryState();
  const conversations = useAiConversations(userId, historyOpen);
  const rename = useRenameConversation(userId);
  const remove = useDeleteConversation(userId);
  const [loadingId, setLoadingId] = useLoadingState();
  const messagesQuery = useAiMessages(loadingId);
  const threadRef = useRef<HTMLDivElement>(null);

  /*
   * Seçilen sohbetin mesajları geldiğinde panele aktarılır. Rapor dosyaları sunucuda
   * kalıcı saklanıyor ve mesajla üst bilgisi geliyor (kart oradan çizilir); eski kayıtlarda
   * rapor mesaj metninde ```html bloğu olarak duruyor, o da karta çevriliyor — yoksa
   * balonda ham HTML duvarı kalırdı.
   */
  useEffect(() => {
    if (!loadingId || !messagesQuery.data) return;
    const conversation = conversations.data?.find((c) => c.conversationId === loadingId);
    openConversation(
      loadingId,
      conversation?.claudeSessionId ?? null,
      messagesQuery.data.map((m) => {
        const { cleaned, artifacts } = extractArtifacts(m.content ?? '');
        const all = [...(m.artifacts ?? []).map(artifactFromDto), ...artifacts];
        return {
          id: m.messageId || newMessageId(),
          role: m.role === 'user' ? ('user' as const) : ('assistant' as const),
          content: cleaned,
          attachments: m.attachments ?? undefined,
          artifacts: all.length ? all : undefined,
        };
      }),
    );
    setLoadingId(null);
    setHistoryOpen(false);
  }, [loadingId, messagesQuery.data, conversations.data, openConversation, setLoadingId, setHistoryOpen]);

  // Yeni mesajda en alta kaydır — akış sırasında da.
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  // Genişlik sürükleme (yalnız masaüstü).
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (!dragRef.current) return;
      setWidth(dragRef.current.startWidth + (dragRef.current.startX - e.clientX));
    };
    const up = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [setWidth]);

  const selectConversation = (conversation: AiConversation) => {
    if (streaming) {
      toast.info(t('ai.stopFirst'));
      return;
    }
    setLoadingId(conversation.conversationId);
  };

  return (
    <aside
      className={cn(
        'bg-bg-elevated border-border fixed z-40 flex flex-col shadow-2xl',
        isMobile ? 'inset-0' : 'inset-y-0 right-0 border-l',
      )}
      style={isMobile ? undefined : { width }}
      role="complementary"
      aria-label="Octo AI"
    >
      {/* Genişlik tutamacı: masaüstünde panelin sol kenarı. */}
      {!isMobile ? (
        <div
          role="separator"
          aria-label={t('ai.resize')}
          aria-orientation="vertical"
          aria-valuenow={width}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          tabIndex={0}
          onPointerDown={(e) => {
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            dragRef.current = { startX: e.clientX, startWidth: width };
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setWidth(width + 24);
            if (e.key === 'ArrowRight') setWidth(width - 24);
          }}
          className="hover:bg-brand-500/40 absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize"
        />
      ) : null}

      <header className="border-border flex items-center gap-2 border-b px-3 py-2">
        <span className="bg-brand-500/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
          <OctoMark className="h-5 w-5" title="Octo AI" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold">Octo AI</h2>
          <AiQuotaBar />
        </div>
        <IconButton
          label={t('ai.history')}
          size="sm"
          onClick={() => setHistoryOpen(!historyOpen)}
          className={cn(historyOpen && 'bg-bg-hover text-fg')}
        >
          <History className="h-4 w-4" />
        </IconButton>
        <IconButton
          label={t('ai.newChat')}
          size="sm"
          onClick={() => {
            if (streaming) stop();
            newChat();
            setHistoryOpen(false);
          }}
        >
          <Plus className="h-4 w-4" />
        </IconButton>
        {isSysRole ? (
          /* Octo AI Ayarları (bağlantılar, bilgi dokümanları, skill'ler) — yalnız sistem rolü. */
          <IconButton
            label={t('ai.admin.title')}
            size="sm"
            onClick={() => {
              openCustomTab({
                kind: 'aiadmin',
                caption: t('ai.admin.title'),
                params: {},
                uniqueKey: 'aiadmin',
              });
              if (isMobile) onClose();
            }}
          >
            <Settings className="h-4 w-4" />
          </IconButton>
        ) : null}
        <IconButton label={t('common:Close')} size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </IconButton>
      </header>

      {!serverReady ? (
        /*
         * Sunucu yapılandırılmamış: sohbet açılamaz. Kısa bir cümle yeter — ayrıntılı
         * kurulum yönergesi panelde yer kaplıyordu; mesaj zaten yalnız sistem rolüne
         * gösteriliyor (bkz. useAiAccess) ve o kişi nereye bakacağını biliyor.
         */
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-4">
          <Alert tone="warning">{t('ai.serverNotReady')}</Alert>
        </div>
      ) : historyOpen ? (
        <AiHistoryList
          conversations={conversations.data ?? []}
          loading={conversations.isLoading || !!loadingId}
          error={conversations.error ? (conversations.error instanceof Error ? conversations.error.message : String(conversations.error)) : null}
          onRetry={() => void conversations.refetch()}
          activeId={conversationId}
          onSelect={selectConversation}
          onRename={(id, title) => title && rename.mutate({ conversationId: id, title })}
          onDelete={(id) => {
            remove.mutate(id);
            if (id === conversationId) newChat();
          }}
        />
      ) : (
        <>
          <div ref={threadRef} className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
            {messages.length === 0 ? <AiWelcome onPick={(text) => void send(text, [])} /> : null}
            {messages.map((message) => (
              <AiMessageBubble key={message.id} message={message} />
            ))}
            {streaming ? (
              <div className="text-fg-muted flex items-center gap-2 pl-9 text-xs">
                <Spinner className="h-3.5 w-3.5" />
                {activeTool ? t('ai.usingTool', { tool: activeTool }) : t('ai.thinking')}
              </div>
            ) : null}
          </div>

          {quotaExceeded ? (
            <Alert tone="warning" className="mx-3 mb-2">
              {t('ai.quotaExceededLong')}
            </Alert>
          ) : null}

          <AiComposer
            disabled={quotaExceeded}
            onSend={(text, files) => void send(text, files)}
            onStop={stop}
          />
        </>
      )}
    </aside>
  );
}

/** Boş sohbet: ne yapabileceğini söyleyen kısa bir karşılama ve birkaç örnek. */
function AiWelcome({ onPick }: { onPick: (text: string) => void }) {
  const { t } = useTranslation();
  const samples = t('ai.samples', { returnObjects: true }) as string[];
  return (
    <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
      <OctoMark className="h-10 w-10" title="Octo AI" />
      <p className="text-sm font-medium">{t('ai.welcome')}</p>
      <p className="text-fg-muted text-xs">{t('ai.welcomeHint')}</p>
      {Array.isArray(samples) ? (
        <div className="mt-1 flex w-full flex-col gap-1.5">
          {samples.map((sample) => (
            <button
              key={sample}
              type="button"
              onClick={() => onPick(sample)}
              className="border-border hover:border-brand-500/60 hover:bg-bg-hover rounded-lg border px-3 py-2 text-left text-xs"
            >
              {sample}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Küçük yerel durumlar — panelin dışında bir anlamı yok.
function useHistoryState() {
  return useLocalToggle(false);
}
function useLoadingState() {
  return useLocalValue<string | null>(null);
}

import { useState } from 'react';
function useLocalToggle(initial: boolean): [boolean, (value: boolean) => void] {
  const [value, setValue] = useState(initial);
  return [value, setValue];
}
function useLocalValue<T>(initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState(initial);
  return [value, setValue];
}
